import { AgendaSlotType, BUSINESS_TIMEZONE } from '@omecare/utils';
import { intlFormat, isValid, toDate } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';
import { popModal, pushModal } from '@/components/modals';
import {
  ConfirmationModalViewModel,
  useConfirmModalViewModel,
} from '@/components/modals/confirmation-modal';
import { useConvertWorkScheduleToOpenSlotMutation } from '@/hooks/mutations/use-convert-work-schedule-to-open-slot';
import { DeleteOccurrenceWorkScheduleSlotResponse } from '@/hooks/mutations/use-delete-occurrence-work-schedule-slot';
import useDeleteWorkScheduleSlotMutation from '@/hooks/mutations/use-delete-work-schedule-slot';
import { StopRecurrenceWorkScheduleSlotResponse } from '@/hooks/mutations/use-stop-recurrence-work-schedule-slot';
import { useUpdateWorkScheduleSlotAssistantModeMutation } from '@/hooks/mutations/use-update-work-schedule-slot-assistant-mode';
import { useUpdateWorkScheduleSlotTagMutation } from '@/hooks/mutations/use-update-work-schedule-slot-tag';
import { GetWorkScheduleSlotItemResponse } from '@/hooks/queries/use-get-office-work-schedules-slots';
import { doctorFullnameTitleTrad } from '@/utils/doctor';
import { useEventOccurencePeriodInformation } from '@/utils/event-slot';
import { SheetViewModel } from '../sheet.viewmodel';

const DATE_TIME_FORMAT: Parameters<typeof intlFormat>[1] = {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

// date-fns v3 `intlFormat` throws `RangeError: Invalid time value` when the
// input is missing or not a valid date. Guard against it so a slot without a
// usable createdAt/updatedAt never crashes the whole sheet.
export const safeIntlFormat = (
  value: Date | string | number | null | undefined,
) => {
  if (value === null || value === undefined) {
    return '';
  }
  const date = toDate(value);
  if (!isValid(date)) {
    return '';
  }
  return intlFormat(date, DATE_TIME_FORMAT, { locale: 'fr' });
};

export type ViewAdminWorkScheduleSlotSheetApi = {
  onAfterDelete: () => void;
  onAfterUpdated: (
    updatedSlot: { id: string } & Partial<GetWorkScheduleSlotItemResponse>,
  ) => void;
};

export type ViewWorkScheduleSlotEventOccurrence = {
  id: string | null;
  start: Date;
  end: Date | null;
  allday: boolean;
  index: number;
  isOnLeave?: boolean;
};

const selectDeleteWorkScheduleSlotClassic = ({
  slotId,
  sheetViewModel,
  onAfterDelete,
}: {
  slotId: string;
  sheetViewModel: SheetViewModel;
  onAfterDelete: ViewAdminWorkScheduleSlotSheetApi['onAfterDelete'];
}) => {
  const {
    mutate: deleteWorkScheduleSlot,
    isPending: deleting,
    isSuccess,
    isError,
  } = useDeleteWorkScheduleSlotMutation();
  const { setModalViewModel } = useConfirmModalViewModel({
    isLoading: deleting,
    isSuccess,
    isError,
  });

  const closeDeleteWorkScheduleSlot = () => {
    sheetViewModel.api.show();
  };

  const cancelDeleteWorkScheduleSlot = () => {
    sheetViewModel.api.show();
  };

  const submitDeleteWorkScheduleSlot = (id: string) => {
    deleteWorkScheduleSlot(id);
  };

  useEffect(() => {
    if (isSuccess) {
      onAfterDelete();
      popModal('ConfirmationModal');
      sheetViewModel.api.close();
    }
  }, [isSuccess]);

  return {
    deleting,
    openModal: () => {
      pushModal('ConfirmationModal', {
        title: 'delete-work-schedule-slot-modal.title',
        description: 'delete-work-schedule-slot-modal.description',
        btnLabel: 'btn.delete',
        btnVariant: 'destructive',
        onConfirm: (viewModel: ConfirmationModalViewModel) => {
          setModalViewModel(viewModel);
          submitDeleteWorkScheduleSlot(slotId);
        },
        onClose: closeDeleteWorkScheduleSlot,
        onCancel: cancelDeleteWorkScheduleSlot,
      });
    },
  };
};

const selectDeleteWorkScheduleSlotRecurrence = ({
  slot,
  eventOccurrence,
  sheetViewModel,
  onAfterUpdated,
}: {
  slot: GetWorkScheduleSlotItemResponse;
  eventOccurrence: ViewWorkScheduleSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
  onAfterUpdated: ViewAdminWorkScheduleSlotSheetApi['onAfterUpdated'];
}) => {
  return {
    openModal: () => {
      pushModal('DeleteWorkScheduleSlotRecurrenceModal', {
        slot: {
          id: slot.id,
          sequence: slot.sequence,
        },
        eventOccurrence: {
          id: eventOccurrence.id,
          start: eventOccurrence.start,
          end: eventOccurrence.end,
          allday: eventOccurrence.allday,
          index: eventOccurrence.index,
        },
        onClose: (props?: { deleted: boolean }) => {
          if (props?.deleted) {
            return;
          }
          sheetViewModel.api.show();
        },
        onCancel: () => {
          sheetViewModel.api.show();
        },
        onDeleteOccurenceSuccess: ({
          data,
        }: {
          data: DeleteOccurrenceWorkScheduleSlotResponse;
        }) => {
          sheetViewModel.api.close();

          onAfterUpdated({
            id: slot.id,
            exdate: data.exdate,
          });
        },
        onStopSeriesSuccess: ({
          data,
        }: {
          data: StopRecurrenceWorkScheduleSlotResponse['data'];
        }) => {
          sheetViewModel.api.close();

          for (const item of data) {
            onAfterUpdated({
              id: item.id,
              rrule: item.rrule,
            });
          }
        },
      });
    },
  };
};

const selectDeleteWorkScheduleSlotState = ({
  slot,
  eventOccurrence,
  sheetViewModel,
  onAfterDelete,
  onAfterUpdated,
}: {
  slot: GetWorkScheduleSlotItemResponse;
  eventOccurrence: ViewWorkScheduleSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
  onAfterDelete: ViewAdminWorkScheduleSlotSheetApi['onAfterDelete'];
  onAfterUpdated: ViewAdminWorkScheduleSlotSheetApi['onAfterUpdated'];
}) => {
  const deleteWorkScheduleSlotClassic = selectDeleteWorkScheduleSlotClassic({
    slotId: slot.id,
    sheetViewModel,
    onAfterDelete,
  });
  const deleteWorkScheduleSlotRecurrence =
    selectDeleteWorkScheduleSlotRecurrence({
      slot,
      eventOccurrence,
      sheetViewModel,
      onAfterUpdated,
    });

  return {
    api: {
      open: () => {
        if (slot.rrule) {
          deleteWorkScheduleSlotRecurrence.openModal();
        } else {
          deleteWorkScheduleSlotClassic.openModal();
        }

        sheetViewModel.api.hide();
      },
    },
  };
};

export const selectViewAdminWorkScheduleSlotSheetViewModel = ({
  slot: initialSlot,
  eventOccurrence,
  sheetViewModel,
  onAfterDelete,
  onAfterUpdated,
}: {
  slot: GetWorkScheduleSlotItemResponse;
  officeId: string;
  eventOccurrence: ViewWorkScheduleSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
} & ViewAdminWorkScheduleSlotSheetApi) => {
  const [slot, setSlot] =
    useState<GetWorkScheduleSlotItemResponse>(initialSlot);
  const getSheetTitle = () => {
    return doctorFullnameTitleTrad(slot.doctorAttending);
  };

  const deleteSlot = selectDeleteWorkScheduleSlotState({
    slot,
    eventOccurrence,
    sheetViewModel,
    onAfterDelete,
    onAfterUpdated,
  });

  const { isPending: updateTagLoading, mutate: updateTagMutation } =
    useUpdateWorkScheduleSlotTagMutation();

  const {
    isPending: updateAssistantModeLoading,
    mutate: updateAssistantModeMutation,
  } = useUpdateWorkScheduleSlotAssistantModeMutation();

  const {
    isPending: convertLoading,
    mutate: convertMutation,
    isSuccess: convertSuccess,
  } = useConvertWorkScheduleToOpenSlotMutation();

  const period = useEventOccurencePeriodInformation({
    event: eventOccurrence,
  });

  const isOnLeave = eventOccurrence.isOnLeave ?? false;

  // The office's calendar day, as the leave API keys it — neither the UTC day
  // nor the viewer's local one.
  const occurrenceDate = formatInTimeZone(
    eventOccurrence.start,
    BUSINESS_TIMEZONE,
    'yyyy-MM-dd',
  );

  return {
    title: getSheetTitle(),
    api: {
      updateTag: (tag?: string) => {
        updateTagMutation(
          { workScheduleSlotId: slot.id, tag },
          {
            onSuccess: () => {
              setSlot((value) => ({
                ...value,
                tag,
              }));
              onAfterUpdated?.({ id: slot.id, tag });
            },
          },
        );
      },
      updateForceWithoutAssistant: (forceWithoutAssistant: boolean) => {
        updateAssistantModeMutation(
          { workScheduleSlotId: slot.id, forceWithoutAssistant },
          {
            onSuccess: (updatedSlot) => {
              // Toggling the assistant mode recomputes derived data (effective
              // assistant state, assistant minutes, SA retrocession), so refresh
              // the full slot snapshot returned by the endpoint instead of
              // patching the single flag locally.
              setSlot(updatedSlot);
              onAfterUpdated?.(updatedSlot);
            },
          },
        );
      },
      updateConsultationMode: (type: AgendaSlotType) => {
        // Switching to fullvisio forces the slot SA server-side, which also
        // recomputes the derived assistant data — refresh the full snapshot.
        updateAssistantModeMutation(
          { workScheduleSlotId: slot.id, type },
          {
            onSuccess: (updatedSlot) => {
              setSlot(updatedSlot);
              onAfterUpdated?.(updatedSlot);
            },
          },
        );
      },
      convertToOpenSlot: () => {
        // Retrocession rate is derived from office defaults on the server.
        convertMutation(
          {
            workScheduleSlotId: slot.id,
            occurrenceDate,
            slotType: 'mixed',
          },
          {
            onSuccess: () => {
              onAfterDelete?.();
              sheetViewModel.api.close();
            },
          },
        );
      },
    },
    data: {
      slot,
      updateTagLoading,
      updateAssistantModeLoading,
      isOnLeave,
      convertLoading,
      convertSuccess,
    },
    information: {
      period,
      doctorRhythm: slot.doctorAttending.rhythm,
      onSiteSlotsWithoutAssistant:
        slot.doctorAttending.onSiteSlotsWithoutAssistant,
      remoteSlotsWithoutAssistant:
        slot.doctorAttending.remoteSlotsWithoutAssistant,
      onSiteSlotsWithAssistant: slot.doctorAttending.onSiteSlotsWithAssistant,
      remoteSlotsWithAssistant: slot.doctorAttending.remoteSlotsWithAssistant,
      rhythmNote: slot.doctorAttending.rhythmNote,
      forceWithoutAssistant: slot.forceWithoutAssistant ?? false,
      createdAt: safeIntlFormat(slot.createdAt),
      updatedAt: safeIntlFormat(slot.updatedAt),
    },
    deleteSlot,
  };
};
